import { NextPage } from "next";
import Script from "next/script";
import { useIntl } from "react-intl";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithoutTemplate } from "@/interfaces/seoWithoutTemplate";
import { AgencyB2B } from "@/strapiQuery/agenciesB2B";
import { Section } from "@/components/common/section/Section";
import {
  ArticleWithPictureWithSubDescription,
  ArticleWithPictureWithSubDescriptionProps,
} from "@/components/common/articleWithPictureWithSubDescription/ArticleWithPictureWithSubDescription";
import {
  PresentationBlock,
  PresentationBlockProps,
} from "./presentationBlock/PresentationBlock";
import {
  CarrouselList,
  CarrouselListProps,
} from "./carrouselList/CarrouselList";
import { ContactForm } from "./contactForm/ContactForm";
import { Accessories, AccessoriesProps } from "./accessories/Accessories";
import { OfferTypes, OfferTypesProps } from "./offerTypes/OfferTypes";
import {
  ValuesDescriptionProps,
  ValuesDescriptions,
} from "./valuesDescriptions/ValuesDescriptions";

import styles from "./SolutionPro.module.scss";

export type SolutionProPageContent = {
  presentationBlock?: PresentationBlockProps;
  brochureLink?: string;
  leftArticleWithPicture?: ArticleWithPictureWithSubDescriptionProps;
  carrouselList?: CarrouselListProps;
  accessoriesList?: AccessoriesProps;
  offerTypes?: OfferTypesProps;
  valuesDescriptionsList?: ValuesDescriptionProps;
  seo: SEO;
  seoWithoutTemplate: SEOWithoutTemplate;
};
type SolutionProPageProps = PageProps &
  SolutionProPageContent & {
    agenciesB2B: AgencyB2B[];
    defaultCanonicalRoute: string;
  };

export const SolutionPro: NextPage<SolutionProPageProps> = ({
  layout,
  seo,
  presentationBlock,
  brochureLink,
  leftArticleWithPicture,
  carrouselList,
  accessoriesList,
  offerTypes,
  valuesDescriptionsList,
  seoWithoutTemplate,
  agenciesB2B,
  defaultCanonicalRoute,
}: SolutionProPageProps) => {
  const intl = useIntl();
  const router = useRouter();

  const [agencyName, setAgencyName] = useState<string>();

  useEffect(() => {
    if (router.query.name !== undefined) {
      setAgencyName(router.query.name as string);
    }
  }, [router.query.name]);

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        pageMeta={seo}
        title={seoWithoutTemplate.title}
        description={seoWithoutTemplate.description}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: `solutionProBreadcrumb` }),
            url: defaultCanonicalRoute,
          },
        ]}
      >
        <Script
          id="insider-pro-page"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: `
          window.insider_object = window.insider_object || {};
          window.insider_object.page = {
              "type": "Content"
          }
          `,
          }}
        />
        <Section sectionStyle={styles.container}>
          {presentationBlock && <PresentationBlock {...presentationBlock} />}
          <ContactForm
            agencyName={agencyName}
            agenciesB2B={agenciesB2B}
            brochureLink={brochureLink}
          />
        </Section>
        {leftArticleWithPicture && (
          <ArticleWithPictureWithSubDescription {...leftArticleWithPicture} />
        )}
        {carrouselList && <CarrouselList {...carrouselList} />}
        {accessoriesList && <Accessories {...accessoriesList} />}
        {offerTypes && <OfferTypes {...offerTypes} />}
        {valuesDescriptionsList && (
          <ValuesDescriptions {...valuesDescriptionsList} />
        )}
      </Layout>
    </>
  );
};
